<script>
import { mapGetters, mapActions } from 'vuex';
import ReportRiskHeader from '@/V2/customers/customer-review-agent/ReportRiskHeader';
import CustomerReviewAgentReportEmptyState from '@/V2/customers/customer-review-agent/CustomerReviewAgentReportEmptyState';
import SalaryCrossCheckVerification from '@/V2/customers/customer-review-agent/SalaryCrossCheckVerification.vue';
import InfoIcon from '@/V2/components/InfoIcon';
import palette from '@inscribe/signal/dist/palette';
import { iSpinner } from '@inscribe/signal';

export default {
  components: {
    ReportRiskHeader,
    SalaryCrossCheckVerification,
    CustomerReviewAgentReportEmptyState,
    InfoIcon,
    iSpinner,
  },
  data() {
    return {
      palette,
      isAgentDataLoading: true,
    };
  },
  computed: {
    ...mapGetters([
      'getCustomerReviewAgentRunsForCustomerUUID',
      'getDocumentReviewAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
    ]),
    effectiveStatus() {
      // prettier-ignore
      return (this.someDocumentUploadingOrProcessing ||
        this.anyDocumentReviewAgentIsRunning)
        ? 'RUNNING'
        : this.latestCustomerReviewAgentRun?.status;
    },
    effectiveRiskLevel() {
      return this.latestCustomerReviewAgentRun?.output?.risk_level || 'UNKNOWN';
    },
    latestCustomerReviewAgentRun() {
      const runs = this.getCustomerReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return runs?.length > 0 ? runs[runs.length - 1] : null;
    },
    anyDocumentReviewAgentIsRunning() {
      const runs = this.getDocumentReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return runs ? runs.some((run) => run.status === 'RUNNING') : false;
    },
    isRunningOrFinished() {
      return (
        this.$store.state.documents.length > 0 &&
        (this.latestCustomerReviewAgentRun ||
          this.someDocumentUploadingOrProcessing ||
          this.anyDocumentReviewAgentIsRunning)
      );
    },
    isFinished() {
      return this.effectiveStatus === 'FINISHED';
    },
    isLoading() {
      return this.$store.state.documentsLoading || this.isAgentDataLoading;
    },
  },
  async mounted() {
    this.isAgentDataLoading = true;
    await this.fetchCustomerReviewAgentRunsForCustomerUUID(
      this.$route.params.customer_id,
    );
    await this.fetchDocumentReviewAgentRunsForCustomerUUID(
      this.$route.params.customer_id,
    );
    this.isAgentDataLoading = false;
  },
  methods: {
    ...mapActions([
      'fetchCustomerReviewAgentRunsForCustomerUUID',
      'fetchDocumentReviewAgentRunsForCustomerUUID',
    ]),
  },
};
</script>

<template>
  <div
    class="customer-review-agent-report d-flex flex-column i-p-10 i-gap-9 h-100"
  >
    <div class="d-flex flex-row align-items-center i-gap-4">
      <div class="name i-text-heading-6 i-text-neutral-120">
        Customer Review Agent
      </div>
      <info-icon
        title="Customer Review Agent"
        description="The AI-powered Customer Review Agent analyses the customer and their documents to provide a risk assessment."
      />
    </div>
    <div
      v-if="isLoading"
      class="d-flex flex-column align-items-center justify-content-center flex-grow-1"
    >
      <i-spinner size="md" class="loading i-pb-10"></i-spinner>
    </div>
    <div v-else-if="isRunningOrFinished" class="d-flex flex-column i-gap-6">
      <ReportRiskHeader
        :risk-level="effectiveRiskLevel"
        confidence="HIGH"
        :status="effectiveStatus"
      ></ReportRiskHeader>

      <div v-if="isFinished" class="d-flex flex-column i-gap-6">
        <div
          class="customer-review-agent-report__section d-flex flex-column align-items-start i-gap-8 i-p-10 analysis-summary"
        >
          <div class="i-text-heading-6 i-text-neutral-120">Analysis</div>
          <div
            class="i-text-body-3 i-text-neutral-120 d-flex flex-column i-gap-8"
          >
            {{ latestCustomerReviewAgentRun.output.rationale }}
          </div>
        </div>

        <div
          v-if="
            latestCustomerReviewAgentRun.output.salary_cross_check_verification
          "
          class="customer-review-agent-report__section d-flex flex-column align-items-start i-gap-8 i-p-10"
        >
          <div class="i-text-heading-6 i-text-neutral-120">Verifications</div>

          <div class="d-flex flex-column align-items-start i-gap-9">
            <SalaryCrossCheckVerification
              :salary-cross-check-verification="
                latestCustomerReviewAgentRun.output
                  .salary_cross_check_verification
              "
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="d-flex flex-column align-items-center justify-content-center flex-grow-1"
    >
      <CustomerReviewAgentReportEmptyState></CustomerReviewAgentReportEmptyState>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-review-agent-report {
  width: 480px;
  overflow-y: auto;
  &__section {
    border: 1px solid $light-neutral-60;
    border-radius: 16px;
  }
}
</style>
